import React from 'react';
import { StyleSheet, Text, View } from './base_components';
import { BusyOverlay, useBusy } from './busy_component';
import StandardBoxList from './standard_box_list';
import LinkTextButton from './buttons/link_text_button';
import { useShowMode, ShowModeSelect } from './show_mode_select';
import VolumeTabBar from './volume_tab_bar';
import TextButton from './buttons/text_button';
import TitleActionLine from './title_action_line';

import MediaStore from '../stores/media_store';

import { byteFormat, dateFormat } from '../tools/util';

const styles = StyleSheet.create({
  noMedia: {
    margin: '2rem',
    color: '#555',
    fontSize: '1.5rem',
    fontWeight: '600',
  },
  deletedText: {
    flex: 1,
    color: 'red',
    fontSize: '1.5rem',
    lineHeight: '2.5rem',
    fontWeight: '500',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    textDecoration: 'underline',
  },
  actionButton: {
    marginLeft: '1rem',
  },
  spacer: {
    flex: 1,
  },
  tabBar: {
    marginLeft: '2rem',
    marginRight: '2rem',
  },
});

type Props = {
  sourceDir: any[];
  volume: any;
  path: string;
};
export default function VolumeDirList(props: Props) {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const source_dir = props.sourceDir;
  const volume = props.volume;
  const volume_id = volume?.volume_id;
  const path = props.path;
  const show_mode = useShowMode();
  const [selected, setSelected] = React.useState([]);
  const show_del = show_mode !== 'active_files';
  const show_active = show_mode !== 'deleted_files';

  let content: any = null;
  let rows: any;
  const media_list = _filterList(
    source_dir,
    path,
    volume_id,
    show_del,
    show_active
  );
  if (media_list.length > 0) {
    rows = media_list.map((item) => {
      const { is_deleted } = item;
      return {
        url: item.path,
        list: [
          is_deleted ? <Deleted text={item.name} /> : item.name,
          item.media ? byteFormat(item.media.file_size) : 'Directory',
          dateFormat(item.media?.last_updated),
        ],
        disable_checkbox: !item.media,
      };
    });
  } else {
    content = <Text style={styles.noMedia}>Empty Directory</Text>;
  }
  const select_media = selected.map((i) => media_list[i]);
  const delete_list = select_media
    .filter((item) => item.is_deleted && item.media)
    .map((i) => i.media);
  const active_list = select_media
    .filter((item) => !item.is_deleted && item.media)
    .map((i) => i.media);

  function _onProgress() {}
  function _onDeletePress() {
    const yes = window.confirm(
      `Are you sure want to delete ${active_list.length} items?`
    );
    if (yes && setBusy()) {
      const opts = {
        volume_id,
        media_list: active_list,
        on_progress: _onProgress,
      };
      MediaStore.deleteList(opts, (err) => {
        if (err) {
          window.alert('Delete failed, please try again.');
        }
        MediaStore.fetchVolumeDir({ volume_id, path });
        clearBusy();
      });
    }
  }
  function _onUndeletePress() {
    const yes = window.confirm(
      `Are you sure want to restore ${delete_list.length} items?`
    );
    if (yes && setBusy()) {
      const opts = {
        volume_id,
        media_list: delete_list,
        on_progress: _onProgress,
      };
      MediaStore.undeleteList(opts, (err) => {
        if (err) {
          window.alert('Restore failed, please try again.');
        }
        MediaStore.fetchVolumeDir({ volume_id, path });
        clearBusy();
      });
    }
  }

  const buttons = (
    <>
      <ShowModeSelect />
      <View style={styles.spacer} />
      <TextButton
        style={styles.actionButton}
        text='Delete'
        type='danger'
        disabled={active_list.length === 0}
        onPress={_onDeletePress}
      />
      <TextButton
        style={styles.actionButton}
        text='Undelete'
        disabled={delete_list.length === 0}
        onPress={_onUndeletePress}
      />
    </>
  );

  return (
    <>
      <TitleActionLine title={path.split('/').pop() || volume?.volume_name}>
        <LinkTextButton
          style={styles.actionButton}
          text='Create Folder'
          url={`/create_folder/${volume_id}${path}`}
        />
        <LinkTextButton
          style={styles.actionButton}
          text='Upload'
          url={`/upload/${volume_id}${path}`}
        />
      </TitleActionLine>
      <VolumeTabBar style={styles.tabBar} volumeId={volume_id} path={path} />
      <StandardBoxList
        title='Objects'
        detail={media_list?.length}
        buttons={buttons}
        headers={['Name', 'Size', 'Last Modified']}
        rows={rows}
        selected={selected}
        onChange={setSelected}
      >
        {content}
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </>
  );
}
function Deleted(props) {
  return <Text style={styles.deletedText}>{props.text}</Text>;
}
function _filterList(source_dir, path, volume_id, show_deleted, show_active) {
  const low_path = path.toLowerCase();
  const output_list: any[] = [];
  const prefix = path?.length === 1 ? '/' : path;
  const slice_length = prefix.length;
  if (source_dir) {
    const { media_list, dir_list } = source_dir;

    media_list.forEach((media) => {
      const { media_path, is_deleted } = media;
      const show = is_deleted ? show_deleted : show_active;
      if (media_path.toLowerCase().indexOf(low_path) === 0 && show) {
        const name = media_path.slice(slice_length);
        output_list.push({
          name,
          is_dir: false,
          media,
          is_deleted,
          path: `/volume/${volume_id}${prefix}${name}`,
        });
      }
    });
    dir_list.forEach((dir) => {
      const { dir_name, file_count } = dir;
      const is_deleted = file_count === 0;
      const show = is_deleted ? show_deleted : show_active;
      if (show) {
        const name = dir_name.slice(slice_length);
        output_list.push({
          name: name + '/',
          is_dir: true,
          media: null,
          is_deleted,
          path: `/volume/${volume_id}${prefix}${name}/`,
        });
      }
    });
  }
  output_list.sort(_outputSort);
  return output_list;
}
const LOCALE_OPTS = { sensitivity: 'accent' };
function _outputSort(a, b) {
  const name_diff = a.name.localeCompare(b.name, undefined, LOCALE_OPTS);
  if (a.is_dir && b.is_dir) {
    return name_diff;
  } else if (a.is_dir) {
    return -1;
  } else if (b.is_dir) {
    return 1;
  } else {
    return name_diff;
  }
}
